import { useEditorStore } from '@/stores/editor/editor-store';
import { useViewerStore } from '@/stores/viewer/viewer-store';
import type { ChecklistItem } from '@/components/progress/Checklist';

function generateChecklistItemId(): string {
  return `item-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Add a new item to a checklist component.
 * Uses dual-store lookup: editor store first, viewer store fallback.
 */
export function addItemToChecklist(
  checklistId: string,
  itemData: { label: string; priority?: string; assignee?: string; dueDate?: string }
): boolean {
  try {
    const editorState = useEditorStore.getState();
    let component = editorState.getComponent?.(checklistId);
    let updateComponent = editorState.updateComponent;

    if (!component) {
      const viewerState = useViewerStore.getState();
      component = viewerState.getComponentById?.(checklistId);
      updateComponent = viewerState.updateComponent;
    }

    if (!component || !updateComponent) {
      console.error(`[ChecklistService] Component "${checklistId}" not found in either store`);
      return false;
    }

    const newItem: ChecklistItem = {
      id: generateChecklistItemId(),
      label: itemData.label,
      checked: false,
      ...(itemData.priority && { priority: itemData.priority as ChecklistItem['priority'] }),
      ...(itemData.assignee && { assignee: itemData.assignee }),
      ...(itemData.dueDate && { dueDate: itemData.dueDate }),
    };

    const currentItems = Array.isArray(component.properties.items)
      ? (component.properties.items as ChecklistItem[])
      : [];

    updateComponent(checklistId, {
      properties: {
        ...component.properties,
        items: [...currentItems, newItem],
      },
    });

    return true;
  } catch (error) {
    console.error('[ChecklistService] Error adding item:', error);
    return false;
  }
}
